import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import AppDialog from "../../shared/ui/AppDialog";
import { useNotify } from "../../shared/ui/feedback/SnackbarProvider";
import { holidayErrorMessage } from "./errors";
import { useHolidayMutations } from "./hooks";
import { BR_UFS, HolidayInput, HolidayKind, KIND_LABEL, LIMITS } from "./types";

export type HolidayFormDialogProps = { open: boolean; onClose: () => void };

const EMPTY: HolidayInput = { date: "", name: "", uf: "", city: "", kind: "holiday", source: "" };
const inRange = (s: string, [min, max]: readonly [number, number]) => s.trim().length >= min && s.trim().length <= max;

/** Novo feriado municipal (S2): data, nome, UF, cidade, tipo e fonte (lei/decreto). */
export default function HolidayFormDialog({ open, onClose }: HolidayFormDialogProps) {
  const notify = useNotify();
  const { create } = useHolidayMutations();
  const [form, setForm] = useState<HolidayInput>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setError(null);
    }
  }, [open]);

  const set = <K extends keyof HolidayInput>(k: K, v: HolidayInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(form.date) && inRange(form.name, LIMITS.name) && !!form.uf && inRange(form.city, LIMITS.city) && inRange(form.source, LIMITS.source);

  const save = async () => {
    setError(null);
    try {
      await create.mutateAsync({ ...form, name: form.name.trim(), city: form.city.trim(), source: form.source.trim() });
      notify({ severity: "success", message: `Feriado “${form.name.trim()}” cadastrado.` });
      onClose();
    } catch (e) {
      setError(holidayErrorMessage(e, "Não foi possível salvar o feriado. Tente de novo."));
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Novo feriado municipal"
      busy={create.isPending}
      actions={
        <>
          <Button onClick={onClose} disabled={create.isPending} sx={{ color: "text.primary" }}>
            Voltar
          </Button>
          <LoadingButton variant="contained" loading={create.isPending} disabled={!valid} onClick={save}>
            Salvar feriado
          </LoadingButton>
        </>
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Data" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} InputLabelProps={{ shrink: true }} required />
        <TextField label="Nome" value={form.name} onChange={(e) => set("name", e.target.value)} required inputProps={{ maxLength: LIMITS.name[1] }} helperText="Ex.: Dia de São José" />
        <Stack direction="row" spacing={2}>
          <TextField select label="UF" value={form.uf} onChange={(e) => set("uf", e.target.value)} required sx={{ width: 120 }}>
            {BR_UFS.map((uf) => (
              <MenuItem key={uf} value={uf}>
                {uf}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Cidade" value={form.city} onChange={(e) => set("city", e.target.value)} required fullWidth inputProps={{ maxLength: LIMITS.city[1] }} />
        </Stack>
        <TextField select label="Tipo" value={form.kind} onChange={(e) => set("kind", e.target.value as HolidayKind)}>
          {(Object.keys(KIND_LABEL) as HolidayKind[]).map((k) => (
            <MenuItem key={k} value={k}>
              {KIND_LABEL[k]}
            </MenuItem>
          ))}
        </TextField>
        <TextField label="Fonte" value={form.source} onChange={(e) => set("source", e.target.value)} required inputProps={{ maxLength: LIMITS.source[1] }} helperText="Lei ou decreto municipal que cria o feriado." />
      </Stack>
    </AppDialog>
  );
}
